import React, { FunctionComponent, useRef } from 'react';
import { DraftTextOrPlaceholderTextConfiguration } from '@/types';
import { isDraftPlaceholderTextConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { MaxSizeHint } from '../../MaxSizeHint';
import { SnapLines } from '../../SnapLines';
import { useConfigurationTransform } from '../../hooks/useConfigurationTransform';
import { useMoveOrEdit } from '../../hooks/useMoveOrEdit';
import { RendererProps } from '../ConfigurationRenderer';
import { ConfigurationHandlesWithZoom } from '../zoomConfigurationTest/ConfigurationHandlesWithZoom';
import { Placeholder } from './Placeholder';
import { useText } from './useText';
import '../../configurationRenderer/textRenderer/TextRenderer.scss';

export const TextRenderer: FunctionComponent<
  RendererProps<DraftTextOrPlaceholderTextConfiguration>
> = ({ configuration, viewMap, hasErrors, isSelected, onShowEdit }) => {
  const textElementRef = useRef<SVGGraphicsElement>(null);

  const minimumScale =
    configuration.text.style.font.minimalSize / configuration.text.style.fontSize;

  const {
    scale,
    maxScale,
    offset,
    rotation,
    infoText,
    isSnapTarget,
    onDelete,
    onMove,
    onPinch,
    onRotate,
    onScale,
    showMaxSizeHint,
    onSetSnapLineActive,
    snapLinesActive,
    snapLines,
  } = useConfigurationTransform(configuration, textElementRef, isSelected, false, minimumScale);

  const [onMoveOrEdit, onPinchOrEdit] = useMoveOrEdit(isSelected, onMove, onPinch, onShowEdit);

  const [textElement, transform] = useText(configuration, offset, rotation, scale);

  return (
    <>
      <SnapLines viewMap={viewMap} lines={snapLines} />
      {isDraftPlaceholderTextConfiguration(configuration) && (
        <Placeholder
          configuration={configuration}
          isSelected={isSelected}
          offset={offset}
          rotation={rotation !== undefined ? rotation : configuration.rotation}
        />
      )}
      <g ref={textElementRef} transform={transform}>
        {textElement}
        <ConfigurationHandlesWithZoom
          active={isSelected}
          configuration={configuration}
          isSnapTarget={isSnapTarget}
          infoText={infoText}
          onDelete={onDelete}
          onMove={onMoveOrEdit}
          onPinch={onPinchOrEdit}
          onRotate={onRotate}
          onScale={onScale}
          scale={scale}
          viewMap={viewMap}
          onSetSnapLineActive={onSetSnapLineActive}
          snapLinesActive={snapLinesActive}
          hasConfigurationErrors={hasErrors}
        />
        <MaxSizeHint
          size={configuration.size}
          currentScale={scale}
          maxScale={maxScale}
          active={showMaxSizeHint}
        />
      </g>
    </>
  );
};
